// Small shared helpers for the vault surfaces (picker + page).
import {
  ArchiveIcon,
  FileAudioIcon,
  FileIcon,
  FileImageIcon,
  FileTextIcon,
  FileVideoIcon,
  PresentationIcon,
  Table2Icon,
  type LucideIcon,
} from "lucide-react";

/** Short initials for avatar chips (e.g. "Jane Doe" → "JD"). */
export function personInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
  }
  return (name.trim().slice(0, 1) || "?").toUpperCase();
}

/** Human-readable byte size (e.g. "2.4 MB"). */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** exponent;
  return `${value >= 10 || exponent === 0 ? Math.round(value) : value.toFixed(1)} ${units[exponent]}`;
}

/** True for image mime types (they get an inline thumbnail preview). */
export function isImageMime(mimeType: string): boolean {
  return mimeType.toLowerCase().startsWith("image/");
}

/** True for HEIC/HEIF images that need conversion before browser preview. */
export function isHeicMime(mimeType: string): boolean {
  const mime = mimeType.toLowerCase();
  return mime === "image/heic" || mime === "image/heif" || mime === "image/heic-sequence";
}

/** True for PDF mime type (browser-native iframe preview). */
export function isPdfMime(mimeType: string): boolean {
  return mimeType.toLowerCase() === "application/pdf";
}

/** True for video mime types (inline frame preview). */
export function isVideoMime(mimeType: string): boolean {
  return mimeType.toLowerCase().startsWith("video/");
}

/** True for audio mime types (inline player preview). */
export function isAudioMime(mimeType: string): boolean {
  return mimeType.toLowerCase().startsWith("audio/");
}

/** True for plain-text mime types. */
export function isTextMime(mimeType: string): boolean {
  return mimeType.toLowerCase().startsWith("text/");
}

/** True for JSON, XML, and other application/* types that are human-readable text. */
export function isStructuredTextMime(mimeType: string): boolean {
  const mime = mimeType.toLowerCase();
  if (mime.startsWith("text/")) return true;
  if (mime === "application/json" || mime === "application/ld+json") return true;
  if (mime === "application/xml" || mime === "text/xml") return true;
  if (mime.endsWith("+json") || mime.endsWith("+xml")) return true;
  if (mime === "application/javascript" || mime === "text/javascript") return true;
  if (mime === "application/x-yaml" || mime === "text/yaml") return true;
  return false;
}

/** True for spreadsheet mime types (xlsx, xls, csv). */
export function isSpreadsheetMime(mimeType: string): boolean {
  const mime = mimeType.toLowerCase();
  return (
    mime.includes("spreadsheet") ||
    mime === "application/vnd.ms-excel" ||
    mime === "text/csv" ||
    mime === "application/csv"
  );
}

const SPREADSHEET_EXTENSIONS = new Set(["csv", "xls", "xlsx"]);

/** True when the item is a spreadsheet by mime type or file extension. */
export function isSpreadsheetFile(item: { name: string; mimeType: string }): boolean {
  if (isSpreadsheetMime(resolveVaultMimeType(item))) return true;
  const extension = item.name.split(".").pop()?.toLowerCase() ?? "";
  return SPREADSHEET_EXTENSIONS.has(extension);
}

const OFFICE_EXTENSIONS = new Set(["doc", "docx", "ppt", "pptx", "numbers"]);

/** True for Microsoft Office and Apple iWork document mime types. */
export function isOfficeMime(mimeType: string): boolean {
  const mime = mimeType.toLowerCase();
  return (
    mime === "application/msword" ||
    mime === "application/vnd.ms-powerpoint" ||
    mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    mime === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
    mime === "application/x-iwork-numbers-sffnumbers" ||
    mime.includes("wordprocessing") ||
    mime.includes("presentation") ||
    mime.includes("numbers")
  );
}

/** True when the item is an office document by mime type or file extension. */
export function isOfficeFile(item: { name: string; mimeType: string }): boolean {
  if (isOfficeMime(resolveVaultMimeType(item))) return true;
  const extension = item.name.split(".").pop()?.toLowerCase() ?? "";
  return OFFICE_EXTENSIONS.has(extension);
}

const ARCHIVE_EXTENSIONS = new Set(["zip", "tar", "gz", "tgz", "bz2", "7z", "rar"]);

/** True for archive mime types. */
export function isArchiveMime(mimeType: string): boolean {
  const mime = mimeType.toLowerCase();
  return (
    mime === "application/zip" ||
    mime === "application/x-zip-compressed" ||
    mime === "application/x-tar" ||
    mime === "application/gzip" ||
    mime === "application/x-gzip" ||
    mime === "application/x-7z-compressed" ||
    mime === "application/x-rar-compressed" ||
    mime === "application/vnd.rar"
  );
}

/** True when the item is an archive by mime type or file extension. */
export function isArchiveFile(item: { name: string; mimeType: string }): boolean {
  if (isArchiveMime(resolveVaultMimeType(item))) return true;
  const extension = item.name.split(".").pop()?.toLowerCase() ?? "";
  return ARCHIVE_EXTENSIONS.has(extension);
}

/** True when the item can show a text snippet preview (text, json, xml, office, archives). */
export function isPreviewableTextItem(item: {
  name: string;
  mimeType: string;
  hasText?: boolean;
}): boolean {
  const mime = resolveVaultMimeType(item);
  return (
    isStructuredTextMime(mime) || item.hasText === true || isOfficeFile(item) || isArchiveFile(item)
  );
}

/** True when vault previews should use the generic text-snippet path instead of a dedicated renderer. */
export function usesVaultTextSnippetPreview(item: {
  name: string;
  mimeType: string;
  hasText?: boolean;
}): boolean {
  const mimeType = resolveVaultMimeType(item);
  if (
    isImageMime(mimeType) ||
    isVideoMime(mimeType) ||
    isAudioMime(mimeType) ||
    isPdfMime(mimeType) ||
    isSpreadsheetFile(item) ||
    isOfficeFile(item) ||
    isArchiveFile(item)
  ) {
    return false;
  }
  return isTextMime(mimeType) || isStructuredTextMime(mimeType) || item.hasText === true;
}

const EXTENSION_MIME: Readonly<Record<string, string>> = {
  "7z": "application/x-7z-compressed",
  aac: "audio/aac",
  avi: "video/x-msvideo",
  bz2: "application/x-bzip2",
  csv: "text/csv",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  flac: "audio/flac",
  gif: "image/gif",
  gz: "application/gzip",
  heic: "image/heic",
  heif: "image/heif",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  json: "application/json",
  jsonl: "application/jsonl",
  m4a: "audio/mp4",
  md: "text/markdown",
  mov: "video/quicktime",
  mp3: "audio/mpeg",
  mp4: "video/mp4",
  numbers: "application/x-iwork-numbers-sffnumbers",
  ogg: "audio/ogg",
  pdf: "application/pdf",
  png: "image/png",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  rar: "application/vnd.rar",
  svg: "image/svg+xml",
  tar: "application/x-tar",
  tgz: "application/gzip",
  txt: "text/plain",
  wav: "audio/wav",
  webm: "video/webm",
  webp: "image/webp",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xml: "application/xml",
  yaml: "text/yaml",
  yml: "text/yaml",
  zip: "application/zip",
};

/** Resolve a stored mime type, falling back to the file extension when needed. */
export function resolveVaultMimeType(item: { name: string; mimeType: string }): string {
  const mime = item.mimeType.trim().toLowerCase();
  if (mime && mime !== "application/octet-stream") return mime;
  const extension = item.name.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_MIME[extension] ?? (mime || "application/octet-stream");
}

/** The type icon for a stored file. */
export function iconForMime(mimeType: string): LucideIcon {
  const mime = mimeType.toLowerCase();
  if (isArchiveMime(mime)) return ArchiveIcon;
  if (isAudioMime(mime)) return FileAudioIcon;
  if (isVideoMime(mime)) return FileVideoIcon;
  if (isImageMime(mime)) return FileImageIcon;
  if (isSpreadsheetMime(mime)) return Table2Icon;
  if (
    mime === "application/vnd.ms-powerpoint" ||
    mime === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
    mime.includes("presentation")
  ) {
    return PresentationIcon;
  }
  if (mime === "application/pdf" || mime.startsWith("text/") || isStructuredTextMime(mime)) {
    return FileTextIcon;
  }
  if (isOfficeMime(mime)) return FileTextIcon;
  return FileIcon;
}

const MIME_LABELS: Readonly<Record<string, string>> = {
  "application/gzip": "Archive",
  "application/json": "JSON",
  "application/msword": "Document",
  "application/vnd.ms-excel": "Spreadsheet",
  "application/vnd.ms-powerpoint": "Presentation",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "Presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Spreadsheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Document",
  "application/x-7z-compressed": "Archive",
  "application/x-iwork-numbers-sffnumbers": "Numbers",
  "application/x-rar-compressed": "Archive",
  "application/x-tar": "Archive",
  "application/x-zip-compressed": "Archive",
  "application/xml": "XML",
  "application/zip": "Archive",
  "text/csv": "CSV",
  "text/markdown": "Markdown",
  "text/yaml": "YAML",
};

/** Short, friendly type label from a mime type (e.g. "PDF", "PNG", "Text"). */
export function typeLabel(mimeType: string): string {
  const mime = mimeType.toLowerCase();
  if (MIME_LABELS[mime]) return MIME_LABELS[mime];
  if (mime === "application/pdf") return "PDF";
  if (mime.startsWith("image/")) return mime.slice("image/".length).toUpperCase();
  if (mime.startsWith("audio/")) return mime.slice("audio/".length).toUpperCase();
  if (mime.startsWith("video/")) return mime.slice("video/".length).toUpperCase();
  if (mime.startsWith("text/")) return "Text";
  if (mime.includes("spreadsheet")) return "Spreadsheet";
  if (mime.includes("wordprocessing")) return "Document";
  if (mime.includes("presentation")) return "Presentation";
  if (mime.includes("numbers")) return "Numbers";
  return mime || "File";
}

/** Compact relative-ish date for a card (falls back to the raw string). */
export function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
